import { useEffect, useState } from 'react';

import { CookieBanner } from './components/CookieBanner';
import { CookieModal } from './components/CookieModal';
import { useCookieConsent } from './hooks/useCookieConsent';
import type { CookieConsentConfig } from './types/cookies.types';

interface CookieConsentAppProps {
  config: CookieConsentConfig;
  showFloatingSettings?: boolean;
}

/**
 * Root cookie consent UI (banner + modal + optional settings control).
 */
export function CookieConsentApp({ config, showFloatingSettings = false }: CookieConsentAppProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const {
    draft,
    hasResponded,
    acceptAll,
    rejectAll,
    updateConsent,
    savePreferences,
    openPreferences,
  } = useCookieConsent(config);

  useEffect(() => {
    const open = () => {
      openPreferences();
      setModalOpen(true);
    };
    window.addEventListener('openCookieSettings', open);
    return () => window.removeEventListener('openCookieSettings', open);
  }, [openPreferences]);

  const openModal = () => {
    openPreferences();
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleAcceptAll = () => {
    acceptAll();
    closeModal();
  };

  const handleRejectAll = () => {
    rejectAll();
    closeModal();
  };

  const handleSave = () => {
    savePreferences();
    closeModal();
  };

  const ui = config.ui ?? {};

  return (
    <>
      <CookieBanner
        visible={!hasResponded}
        cookiesPolicyUrl={config.cookiesPolicyUrl}
        ui={ui}
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectAll}
        onCustomize={openModal}
      />
      <CookieModal
        open={modalOpen}
        draft={draft}
        categories={config.categories}
        ui={ui}
        onClose={closeModal}
        onToggle={updateConsent}
        onSave={handleSave}
        onAcceptAll={handleAcceptAll}
      />
      {showFloatingSettings && hasResponded && (
        <button
          type="button"
          className="cookie-floating-settings"
          onClick={openModal}
          aria-label={ui.openSettingsAria ?? 'Open cookie settings'}
          title={ui.openSettingsTitle ?? ui.settingsTitle ?? 'Cookie settings'}
        >
          🍪
        </button>
      )}
    </>
  );
}
